import { isAxiosError } from "axios";
import { useSnackStore, type IShowSnack } from "@/stores/snackStore";
import { i18n } from "@/locales";

interface ErrorItem {
  attr: string | null;
  code: string;
  detail: string;
  pending?: Array<{ id: string; title: string; version: number }>;
}

export enum EErrorType {
  VALIDATION_ERROR = "validation_error",
  CLIENT_ERROR = "client_error",
  SERVER_ERROR = "server_error",
}

export const TOKEN_NOT_VALID_ERROR_CODE = "token_not_valid";
export const TERMS_NOT_ACCEPTED_ERROR_CODE = "terms_not_accepted";
/**
 * First segment of every terms query key (`TermsService.queryKey[0]`, derived from the
 * `/api/v1/terms` slug). Lives here rather than being imported from the service so the axios
 * interceptor can reference it without the `api/index → terms → crud → api/index` import cycle.
 * Kept in sync by `terms.queryKeyRoot.test.ts`.
 */
export const TERMS_QUERY_KEY_ROOT = "terms";

export type TVeeSetErrors = (
  fields: Partial<Record<string, string | string[] | undefined>>
) => void;

export interface ErrorSchema {
  type: EErrorType;
  errors: ErrorItem[];
}

/**
 * Detail of the validation error raised on `subscription_product` — most notably when a product is
 * not offered in the owner's country. Already an end-user sentence naming the product and both
 * countries, so it can be shown at the field verbatim.
 */
export function subscriptionProductErrorDetail(error: unknown): string | null {
  if (!isAxiosError<ErrorSchema>(error)) return null;
  const data = error.response?.data;
  if (data?.type !== EErrorType.VALIDATION_ERROR) return null;
  return data.errors.find((item) => item.attr === "subscription_product")?.detail ?? null;
}

export function isTokenNotValidError(error: unknown): boolean {
  return (
    isAxiosError<ErrorSchema>(error) &&
    error.response?.data.type === EErrorType.CLIENT_ERROR &&
    error.response?.data.errors.some((e) => e.code === TOKEN_NOT_VALID_ERROR_CODE)
  );
}

/** True when the axios client aborted the request itself for running past its configured timeout. */
export function isRequestTimeoutError(error: unknown): boolean {
  return isAxiosError(error) && error.code === "ECONNABORTED";
}

interface ISnackable {
  showSnack: (showSnack: IShowSnack) => void;
  showError: (message: unknown) => void;
  showPriorityError: (message: unknown) => void;
}

function flattenErrors(errors: Record<string, string[]>): string[] {
  return Object.entries(errors).map(([field, msgs]) => {
    return field ? `${field}: ${msgs.join(", ")}` : msgs.join(", ");
  });
}

function convertSnackSchema(data: ErrorSchema): Record<string, string[]> {
  return data.errors.reduce(
    (acc, { attr, detail }) => {
      const rawKey = attr ?? "";
      const prettyKey = rawKey
        .split(/[._]+/)
        .map((segment) => {
          if (/^\d+$/.test(segment)) {
            // Optional: turn “0” → “#1”, “1” → “#2”
            return `#${Number(segment) + 1}`;
          }
          // Capitalize the first letter, leave the rest
          return segment.charAt(0).toUpperCase() + segment.slice(1);
        })
        .join(" ");

      (acc[prettyKey] ||= []).push(detail);
      return acc;
    },
    {} as Record<string, string[]>
  );
}

function convertErrorSchema(data: ErrorSchema): Record<string, string[]> {
  return data.errors.reduce(
    (acc, { attr, code }) => {
      const rawKey = attr ?? "";
      const t = i18n.global.t as (key: string, params?: Record<string, unknown>) => string;
      (acc[rawKey] ||= []).push(t(`generic.errors.${code}`));
      return acc;
    },
    {} as Record<string, string[]>
  );
}

function headerToString(value: unknown): string {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === "string").join(", ");
  }
  return "";
}

// Simpler handler for standard Django viewset endpoints that don't need form field mapping.
// Handles: drf-spectacular typed errors, standard DRF {detail}, HTML pages, and status-based fallbacks.
export function djangoViewsetErrorHandler(snackStore?: ISnackable, setErrors?: TVeeSetErrors) {
  const store = snackStore ?? useSnackStore();
  return (error: unknown) => {
    // Handled globally by the terms guard in App.vue
    if (
      isAxiosError<ErrorSchema>(error) &&
      error.response?.data?.errors?.some((e) => e.code === TERMS_NOT_ACCEPTED_ERROR_CODE)
    ) {
      return;
    }

    if (!isAxiosError(error) || !error.response) {
      const msg = typeof error === "string" ? error : (error as Error).message;
      store.showError(msg);
      return;
    }

    const { status, data, headers } = error.response;
    const contentType = headerToString(headers["content-type"]);

    // drf-spectacular typed error schema
    if (isAxiosError<ErrorSchema>(error) && Object.values(EErrorType).includes(data?.type)) {
      if (setErrors) {
        setErrors(convertErrorSchema(error.response.data));
      }
      const prettyErrs = convertSnackSchema(error.response.data);
      const messages = flattenErrors(prettyErrs);
      status >= 500 ? store.showPriorityError(messages) : store.showError(messages);
      return;
    }

    // Standard DRF {detail: "..."} — most common from viewsets, sometimes customised
    if (typeof data?.detail === "string") {
      status >= 500 ? store.showPriorityError(data.detail) : store.showError(data.detail);
      return;
    }

    // Legacy/custom viewset shape: { error: "..." }
    if (
      typeof data === "object" &&
      data !== null &&
      typeof (data as { error?: unknown }).error === "string"
    ) {
      const msg = (data as { error: string }).error;
      status >= 500 ? store.showPriorityError(msg) : store.showError(msg);
      return;
    }

    // HTML (Django debug page or server error)
    if (contentType.includes("text/html")) {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(data as string, "text/html");
        const text = doc.body.textContent?.trim() || "";
        store.showPriorityError(text || `Server returned an HTML error page (status ${status}).`);
      } catch {
        store.showPriorityError(`Server error (${status}). Please try again later.`);
      }
      return;
    }

    // Status-based fallbacks
    const fallbackMsg =
      status >= 500
        ? `Server error (${status}). Please try again later.`
        : status === 404
          ? "Resource not found."
          : status === 403
            ? "You don't have permission to do this."
            : status === 401
              ? "Authentication required."
              : (error as Error).message;
    status >= 500 ? store.showPriorityError(fallbackMsg) : store.showError(fallbackMsg);
  };
}

export function djangoErrorHandler(snackStore?: ISnackable, setErrors?: TVeeSetErrors) {
  const store = snackStore ?? useSnackStore();
  return (error: unknown) => {
    if (!isAxiosError(error) || !error.response) {
      const msg = typeof error === "string" ? error : (error as Error).message;
      store.showError(msg);
      return;
    }

    const { status, data, headers } = error.response;
    const contentType = headerToString(headers["content-type"]);

    if (
      isAxiosError<ErrorSchema>(error) &&
      (error.response?.data.type === EErrorType.VALIDATION_ERROR ||
        error.response?.data.type === EErrorType.CLIENT_ERROR ||
        error.response?.data.type === EErrorType.SERVER_ERROR)
    ) {
      const fieldErrs = convertErrorSchema(error.response.data);
      if (setErrors) {
        setErrors(fieldErrs);
      }
      const prettierFieldErrs = convertSnackSchema(error.response.data);
      if (error.response.data.errors.at(0)?.code === "not_found") {
        store.showError(flattenErrors(prettierFieldErrs));
        return;
      }
      store.showPriorityError(flattenErrors(prettierFieldErrs));
      return;
    }
    if (contentType.includes("text/html")) {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(data as string, "text/html");
        const text = doc.body.textContent?.trim() || "";
        store.showError(text || `Server returned an HTML error page (status ${status}).`);
      } catch (error) {
        logError(error, {
          context: { feature: "djangoErrorHandler", step: "DOMParser" },
        });
        store.showError(`Server error (${status}). Please try again later.`);
      }
      return;
    }
    if (error.response.data.error) {
      store.showError(error.response.data.error);
      return;
    }
    const fallbackMsg =
      status >= 500
        ? `Server error (${(error as any).message}). Please try again later.`
        : (error as any).message;
    store.showError(fallbackMsg);
  };
}
